import {Alert, Button, Checkbox, FileInput, Group, Input, Modal, Radio} from "@mantine/core";
import React, {useEffect, useState} from "react";
import {AttachmentType, Stage, UploadAttachment} from "../../../../../types/domain";
import AttachmentTypeService from "../../../../../services/attachmenttype.service";
import {MAX_UPLOAD_SIZE_BYTES} from "../../../../../types/constants";

/**
 * @author KMU
 * Class to define a upload modal
 */

type UploadModalProps = {
    opened: boolean,
    close: () => void,
    attachments: Array<UploadAttachment>
    onAdd: (attachment: UploadAttachment) => void,
    allStages: Array<Stage>,
    planStages: Array<Stage>
};

export function UploadModal({opened, close,
                                attachments, onAdd,
                                allStages, planStages}: UploadModalProps) {
    const [types, setTypes] = useState<AttachmentType[]>([]);
    const [selType, setSelType] = useState<string>("");
    const [selFile, setSelFile] = useState<File | null>(null);
    const [isTopicSpecific, setIsTopicSpecific] = useState<boolean>(false);
    const [selStages, setSelStages] = useState<string[]>([]);
    const [selSolutionFile, setSelSolutionFile] = useState<File | null>(null);
    const [alert, setAlert] = useState({
        show: false,
        message: ""
    });

    const allowedTypes = ["image/png", "image/jpeg", "application/pdf",
        "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];

    // preselect plan stages when modal opens or planStages change
    useEffect(() => {
        setSelStages(planStages.map(s => s.name));
    }, [planStages, opened]);

    useEffect(() => {
        AttachmentTypeService.getAll()
            .then((response: any) => {
                setTypes(response.data);
            })
            .catch((e: Error) => {
                console.log(e);
            });
    }, []);

    /**
     * Save attachment in temporary list
     */
    function saveUpload() {
        if (!selFile || !selType) {
            setAlert({show: true, message: "Bitte füllen Sie alle Felder aus!"});
        } else if (selStages.length === 0) {
            setAlert({ show: true, message: "Es muss mindestens ein Stadium ausgewählt werden!" });
        } else if (attachments.find(a => a.file?.name === selFile.name)) {
            setAlert({show: true,message: "Dieses Dokument wurde bereits hochgeladen!"});
        } else {
            onAdd({
                type: types.find(t => t.name === selType)!,
                name: selFile.name,
                file: selFile,
                isTopicSpecific: isTopicSpecific,
                topic: null,
                stages: allStages.filter(s => selStages.includes(s.name)),
                solutionFile: selSolutionFile
            })
            setSelFile(null);
            setSelType("");
            setIsTopicSpecific(false);
            setSelStages(planStages.map(s => s.name));  // reset to plan stages
            setSelSolutionFile(null);
            close();
            setAlert({show: false, message: ""});
        }
    }

    /**
     * Handle selected file on file change
     * @param file selected file
     */
    function onFileChange(file: File | null) {
        if (!file) return;

        if (!allowedTypes.includes(file.type)){
            setAlert({show: true, message: "Es dürfen nur Bilder, PDFs oder Word-Dateien hochgeladen werden!"});
            setSelFile(null);
        } else if (file.size > MAX_UPLOAD_SIZE_BYTES) {
            setAlert({show: true, message: "Die Datei darf maximal 20MB groß sein!"});
            setSelFile(null);
        } else {
            setSelFile(file);
            setAlert({show: false, message: ""});
        }
    }

    /**
     * Handle solution file change
     * @param file
     */
    function onSolutionFileChange(file: File | null) {
        if (!file) {
            setSelSolutionFile(null);
            return;
        }
        if (!allowedTypes.includes(file.type)) {
            setAlert({ show: true, message: "Es dürfen nur Bilder, PDFs oder Word-Dateien hochgeladen werden!" });
            setSelSolutionFile(null);
        } else if (file.size > MAX_UPLOAD_SIZE_BYTES) {
            setAlert({ show: true, message: "Die Datei darf maximal 20MB groß sein!" });
            setSelSolutionFile(null);
        } else {
            setSelSolutionFile(file);
            setAlert({ show: false, message: "" });
        }
    }

    return (
        <>
            <Modal opened={opened} onClose={close} title="Anhang hinzufügen"
            size="auto">
                <Radio.Group
                    className="newExerciseField"
                    value={selType}
                    onChange={setSelType}
                    label="Art des Anhangs"
                    withAsterisk
                >
                    <Group>
                        {types
                            .filter(type => type.name !== 'Lösungsblatt')  // remove from options
                            .map(type => <Radio key={type.name} label={type.name} value={type.name}/>)
                        }
                    </Group>
                </Radio.Group>
                <FileInput
                    className="newExerciseField"
                    value={selFile}
                    onChange={onFileChange}
                    placeholder="Datei auswählen"
                    label="Anhang"
                    accept={allowedTypes.toString()}
                    withAsterisk
                />
                {selType === 'Arbeitsblatt' &&
                    <FileInput
                        className="newExerciseField"
                        value={selSolutionFile}
                        onChange={onSolutionFileChange}
                        placeholder="Lösungsblatt auswählen (optional)"
                        label="Lösungsblatt"
                        accept={allowedTypes.toString()}
                    />
                }
                <Checkbox.Group
                    className="newExerciseField"
                    value={selStages}
                    onChange={setSelStages}
                    label="Geeignete Stadien"
                    withAsterisk
                >
                    <Group>
                        {allStages.map(stage => (
                            <Checkbox key={stage.name} label={stage.name} value={stage.name}/>
                        ))}
                    </Group>
                </Checkbox.Group>
                <Input.Wrapper
                    className="newExerciseField"
                    label="Themenspezifisch">
                    <Checkbox
                        className="newExerciseField"
                        checked={isTopicSpecific}
                        onChange={(e) => setIsTopicSpecific(e.currentTarget.checked)}
                        label="Anhang ist themenspezifisch"
                    />
                </Input.Wrapper>
                <Button onClick={saveUpload}>
                    Hinzufügen
                </Button>
                {
                    alert.show ?
                        <Alert id={'uploadAlert'} w={"100%"} title="Upload fehlgeschlagen" ta={"left"} color="red"
                               mt={"10px"}>
                            {alert.message}
                        </Alert> : <></>
                }
            </Modal>
        </>
    )
}